import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import api from 'api'
import {
  CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST,
  CACHE_TAG_ACCOUNT_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
  CACHE_TAG_INVOICE_LIST,
  CACHE_TAG_WORK_ORDER_LIST,
} from 'api/cacheTagTypes'
import { ReactComponent as InvoiceIcon } from 'assets/Invoice.svg'
import CreateInvoice from 'components/customer-details/content/CreateInvoice'
import T from 'T'

// Invoice/PDF generation is async; invalidating immediately can refetch before the new
// invoice is queryable and leave billing/AR stale. Delay to match the in-section create flow.
const INVOICE_REFETCH_DELAY_MS = 4000

interface CreateInvoiceIconButtonProps {
  accountId: string
  billingProfileDetails?: Record<string, unknown>
  size?: 'small' | 'medium' | 'large'
}

const CreateInvoiceIconButton = ({ accountId, billingProfileDetails = {}, size = 'medium' }: CreateInvoiceIconButtonProps) => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleOpen = useCallback(() => setIsOpen(true), [])
  const handleClose = useCallback(() => setIsOpen(false), [])

  const handleInvoiceCreated = useCallback(() => {
    if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current)
    refetchTimeoutRef.current = setTimeout(() => {
      refetchTimeoutRef.current = null
      dispatch(
        api.util.invalidateTags([
          CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
          CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
          CACHE_TAG_ACCOUNT_INVOICE_LIST,
          CACHE_TAG_INVOICE_LIST,
          CACHE_TAG_WORK_ORDER_LIST,
          CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST,
        ])
      )
    }, INVOICE_REFETCH_DELAY_MS)
  }, [dispatch])

  useEffect(
    () => () => {
      if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current)
    },
    []
  )

  return (
    <>
      <Tooltip arrow title={T.CREATE_INVOICE}>
        <IconButton size={size} onClick={handleOpen}>
          <SvgIcon inheritViewBox component={InvoiceIcon} />
        </IconButton>
      </Tooltip>
      {isOpen && (
        <CreateInvoice
          accountId={accountId}
          billingProfileDetails={billingProfileDetails}
          callback={handleInvoiceCreated}
          isOpen={isOpen}
          onClose={handleClose}
        />
      )}
    </>
  )
}

export default CreateInvoiceIconButton
